import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { WLIDS } from 'Constants';
import { store } from 'Models/Store';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import LoginFlow from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/LoginStep/LoginFlow';
import GiftCertificateForm, { GiftCertificateFormSubmitData, GiftCertificateFormRef } from './GiftCertificateForm';
import { GiftVoucherPaymentStep } from './GiftVoucherPaymentStep';
import { GiftVoucherStepIndicator } from './GiftVoucherStepIndicator';
import { VoucherTicket } from './VoucherTicket';
import { purchaseGiftCertificate, abandonVoucherTransaction } from 'Services/Api/_HumanWritten/GiftCertificateService';
import alertToast from 'Util/ToastifyUtils';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import LoaderInsideButton from 'Views/Components/Spinner/LoaderInsideButton';
import { formatStandardDate } from 'Util/_HumanWritten/DateUtils';
import { PAYMENT_FORM_ID } from 'Views/Components/_HumanWritten/Payment/usePayment';
import { newAlertModal } from 'Views/Components/Modal/NewAlertModal';
import { ReservationTimer } from 'Views/Components/_HumanWritten/ReservationTimer/ReservationTimer';
import { savePendingVoucherPurchase } from './VoucherPurchasePendingFlag';
import { PurchaseTermsAcceptance } from 'Views/Components/_HumanWritten/Payment/PurchaseTermsAcceptance';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { UserEntity } from 'Models/Entities';
import {
	RegisterCustomerStep,
	RegisterCustomerStepFooter,
} from 'Views/Components/_HumanWritten/RegisterCustomer/RegisterCustomerStep';

type ModalStep = 'login' | 'details' | 'registerCustomer' | 'terms' | 'payment' | 'success';

const VOUCHER_REGISTER_CUSTOMER_FORM_ID = 'voucher-register-customer';

interface PurchaseGiftVoucherModalProps {
	isOpen: boolean;
	onClose: () => void;
	prefillCustomerId?: string;
	prefillCustomerName?: string;
	/**
	 * The customer record behind the prefill. The id and name alone set the form's hidden values but
	 * leave the customer picker itself blank, because it renders from the selected UserEntity — so
	 * opening this from a customer's own account looked like no customer had been chosen.
	 */
	prefillCustomer?: UserEntity;
}

function PurchaseGiftVoucherModal({
	isOpen,
	onClose,
	prefillCustomerId,
	prefillCustomerName,
	prefillCustomer,
}: PurchaseGiftVoucherModalProps) {
	const [step, setStep] = useState<ModalStep>('details');
	const [transactionId, setTransactionId] = useState<string>('');
	const [voucherCode, setVoucherCode] = useState<string>('');
	const [loading, setLoading] = useState(false);
	const [paymentProcessing, setPaymentProcessing] = useState(false);
	const [formData, setFormData] = useState<GiftCertificateFormSubmitData | null>(null);
	// Unvalidated snapshot of the details form, taken when detouring to the register-customer
	// step so the form can be restored (with the new customer selected) on return.
	const [draftData, setDraftData] = useState<Partial<GiftCertificateFormSubmitData> | null>(null);
	const [registerCustomerLoading, setRegisterCustomerLoading] = useState(false);
	const [registrationComplete, setRegistrationComplete] = useState(false);
	const [loginMode, setLoginMode] = useState<string>('initial');
	const [loginLoading, setLoginLoading] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);
	// Presses of Continue with the terms unticked. See PurchaseTermsAcceptance for why it is a count.
	const [termsPromptCount, setTermsPromptCount] = useState(0);
	const formRef = useRef<GiftCertificateFormRef>(null);
	const loginResetRef = useRef<(() => void) | null>(null);

	const hasTerms = !!whiteLabelStore.config?.defaultVoucherTerms;

	const scrollModalToTop = useCallback(() => {
		const modalBody = document.querySelector('.purchase-gift-voucher-modal .new-modal__message');
		modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	// This component stays mounted between opens (parents only toggle isOpen), so
	// state has to be reset explicitly. Reset on *close* rather than open: the inner
	// form is unmounted by then, so it re-mounts from cleared state on the next open.
	// (Resetting on open runs after react-modal has already re-mounted the form with
	// the stale formData as initialValues, leaving the old values in place.)
	useEffect(() => {
		if (isOpen) {
			// Choose the starting step based on auth state at open time.
			setStep(store.loggedIn ? 'details' : 'login');
		} else {
			setStep('details');
			setTransactionId('');
			setVoucherCode('');
			setLoading(false);
			setPaymentProcessing(false);
			setFormData(null);
			setDraftData(null);
			setRegistrationComplete(false);
			setTermsAccepted(false);
			setTermsPromptCount(0);
		}
	}, [isOpen]);

	const handleLoginSuccess = useCallback(() => {
		setStep('details');
	}, []);

	const handleAddNewCustomer = useCallback(() => {
		// Snapshot the details form so it can be restored when the detour finishes.
		setDraftData(formRef.current?.capture() ?? null);
		setStep('registerCustomer');
		scrollModalToTop();
	}, [scrollModalToTop]);

	const handleRegisterCustomerSuccess = useCallback(
		(customer: UserEntity) => {
			setDraftData(prev => ({
				...(prev ?? {}),
				userId: customer.id,
				customerName: `${customer.firstName} ${customer.lastName}`,
				selectedCustomer: customer,
			}));
			setStep('details');
			scrollModalToTop();
		},
		[scrollModalToTop],
	);

	const handleFormSubmit = useCallback(
		async (data: GiftCertificateFormSubmitData) => {
			// The submitted data supersedes any draft from a register-customer detour.
			setDraftData(null);

			// If there's already an in-progress transaction, go straight to payment
			if (transactionId) {
				setFormData(data);
				setStep('payment');
				return;
			}

			// If terms exist and we haven't shown them yet, go to terms first
			if (hasTerms && !termsAccepted && step !== 'terms') {
				setFormData(data);
				setStep('terms');
				scrollModalToTop();
				return;
			}

			setLoading(true);
			setFormData(data);
			try {
				const { data: responseData } = await purchaseGiftCertificate({
					amount: data.amount,
					isGift: data.isGift,
					recipientEmail: data.recipientEmail,
					message: data.message,
					userId: data.userId,
					expiryDate: data.expiryDate,
				});

				const txId = responseData.transaction?.id;
				if (txId) {
					setTransactionId(txId);
					setVoucherCode(responseData.code ?? '');
					setStep('payment');
				} else {
					alertToast('Unable to create gift certificate payment', 'error');
				}
			} catch (e: any) {
				if (e.response?.status === 400) {
					alertToast(e.response.data || 'Unable to create gift certificate', 'error');
				} else if (e.response?.status === 401) {
					alertToast('Please log in to purchase a gift certificate', 'error');
				} else {
					alertToast('An error occurred. Please try again.', 'error');
				}
			} finally {
				setLoading(false);
			}
		},
		[transactionId, hasTerms, termsAccepted, step, scrollModalToTop],
	);

	const handlePaymentSuccess = useCallback(() => {
		setStep('success');
	}, []);

	const handlePaymentProcessingChange = useCallback(
		(processing: boolean) => {
			setPaymentProcessing(processing);
			// Scroll to top when payment finishes (error case)
			if (!processing) {
				scrollModalToTop();
			}
		},
		[scrollModalToTop],
	);

	// Block navigation when in details or payment step
	const shouldBlockNavigation =
		isOpen && (step === 'details' || step === 'registerCustomer' || step === 'terms' || step === 'payment');
	const shouldBlockRef = useRef(shouldBlockNavigation);
	const isShowingConfirmRef = useRef(false);

	useEffect(() => {
		shouldBlockRef.current = shouldBlockNavigation;
	}, [shouldBlockNavigation]);

	// Push a history entry when modal opens so back button can be intercepted
	useEffect(() => {
		if (shouldBlockNavigation) {
			window.history.pushState({ voucherModal: true }, '');
			return () => {
				// Clean up the pushed history entry when no longer blocking
				if (window.history.state?.voucherModal) {
					window.history.back();
				}
			};
		}
		return undefined;
	}, [shouldBlockNavigation]);

	// Handle browser back button and refresh
	useEffect(() => {
		if (!shouldBlockNavigation) {
			return undefined;
		}

		const handlePopState = () => {
			if (!shouldBlockRef.current || isShowingConfirmRef.current) {
				return;
			}

			// Push state back so the URL doesn't change
			window.history.pushState({ voucherModal: true }, '');

			isShowingConfirmRef.current = true;
			newConfirmModal('Are you sure?', 'You will lose your progress on this voucher purchase.', {
				confirmText: 'Leave',
				cancelText: 'Stay',
			})
				.then(() => {
					isShowingConfirmRef.current = false;
					abandonTransaction();
					// Go back to remove our pushed state
					window.history.back();
					onClose();
				})
				.catch(() => {
					isShowingConfirmRef.current = false;
				});
		};

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};

		window.addEventListener('popstate', handlePopState);
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('popstate', handlePopState);
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [shouldBlockNavigation, onClose]);

	const abandonTransaction = useCallback(() => {
		if (transactionId) {
			abandonVoucherTransaction(transactionId).catch(() => {});
		}
	}, [transactionId]);

	const handleTimerExpiry = useCallback(() => {
		abandonTransaction();
		newAlertModal('Session has expired', 'You ran out of time. Please start a new voucher purchase.', {
			cancelText: 'OK',
			shouldCloseOnOverlayClick: false,
			shouldCloseOnEsc: false,
		}).then(() => {
			setTransactionId('');
			setVoucherCode('');
			setFormData(null);
			setPaymentProcessing(false);
			setStep('details');
		});
	}, [abandonTransaction]);

	const handleClose = () => {
		if (step === 'details' || step === 'registerCustomer' || step === 'terms' || step === 'payment') {
			newConfirmModal('Are you sure?', 'You will lose your progress on this voucher purchase.', {
				confirmText: 'Leave',
				cancelText: 'Stay',
			})
				.then(() => {
					abandonTransaction();
					onClose();
				})
				.catch(() => {});
		} else {
			onClose();
		}
	};

	// Build steps array based on whether login is needed
	const needsLogin = !store.loggedIn;
	const steps = needsLogin
		? [{ label: 'Login', icon: 'lock' }, 'Details', ...(hasTerms ? ['Terms'] : []), 'Payment', 'Complete']
		: ['Details', ...(hasTerms ? ['Terms'] : []), 'Payment', 'Complete'];

	const termsOffset = hasTerms ? 1 : 0;
	// registerCustomer is a detour from the details step, so the indicator stays on Details.
	const stepIndexMap: Record<ModalStep, number> = needsLogin
		? {
				login: 0,
				details: 1,
				registerCustomer: 1,
				terms: 2,
				payment: 2 + termsOffset,
				success: 3 + termsOffset,
			}
		: {
				login: -1,
				details: 0,
				registerCustomer: 0,
				terms: 1,
				payment: 1 + termsOffset,
				success: 2 + termsOffset,
			};

	const currentStepIndex = stepIndexMap[step];
	// The countdown only exists once payment is being taken against a real held transaction.
	const showTimer = step === 'payment' && !!transactionId;

	const getSuccessMessage = () => {
		if (formData?.giftType === 'gift' && formData.recipientEmail) {
			return (
				<p>
					The voucher has been sent to <strong>{formData.recipientEmail}</strong>
					{formData.customerName && (
						<>
							{' '}
							on behalf of <strong>{formData.customerName}</strong>
						</>
					)}
					.
				</p>
			);
		}
		if (formData?.giftType === 'for-customer' && formData.customerName) {
			return (
				<p>
					The voucher has been added to <strong>{formData.customerName}</strong>&apos;s account.
				</p>
			);
		}
		return <p>Your voucher has been added to your account.</p>;
	};

	const renderStepContent = () => {
		switch (step) {
			case 'login':
				return (
					<div className="purchase-gift-voucher__login">
						<LoginFlow
							afterLoginSuccess={handleLoginSuccess}
							formId="purchase-voucher-login"
							registrationMessage={<FormattedMessage {...WLIDS.auth.voucherRegistrationDescription} />}
							onRegistrationComplete={() => {
								setRegistrationComplete(true);
								savePendingVoucherPurchase();
							}}
							onModeChange={setLoginMode}
							onLoadingChange={setLoginLoading}
							resetRef={loginResetRef}
						/>
					</div>
				);
			case 'details':
				return (
					<GiftCertificateForm
						ref={formRef}
						onSubmit={handleFormSubmit}
						onAddNewCustomer={store.isStaff ? handleAddNewCustomer : undefined}
						initialValues={
							draftData ??
							formData ??
							(prefillCustomerId
								? {
										giftType: 'for-customer',
										userId: prefillCustomerId,
										customerName: prefillCustomerName,
										selectedCustomer: prefillCustomer,
									}
								: undefined)
						}
					/>
				);
			case 'registerCustomer':
				return (
					<RegisterCustomerStep
						formId={VOUCHER_REGISTER_CUSTOMER_FORM_ID}
						onSuccess={handleRegisterCustomerSuccess}
						onLoadingChange={setRegisterCustomerLoading}
					/>
				);
			case 'terms':
				return (
					<PurchaseTermsAcceptance
						title="Voucher Terms and Conditions"
						termsHtml={whiteLabelStore.config?.defaultVoucherTerms ?? ''}
						accepted={termsAccepted}
						onAcceptedChange={setTermsAccepted}
						promptCount={termsPromptCount}
						testId="voucher-terms-accept"
					/>
				);
			case 'payment':
				return (
					<GiftVoucherPaymentStep
						transactionId={transactionId}
						customerUserId={formData?.userId}
						onSuccess={handlePaymentSuccess}
						onProcessingChange={handlePaymentProcessingChange}
					/>
				);
			case 'success':
				return (
					<div className="purchase-gift-voucher__success">
						<div className="purchase-gift-voucher__success-icon">
							<svg className="animated-checkmark" viewBox="0 0 52 52">
								<circle className="animated-checkmark__circle" cx="26" cy="26" r="24" fill="none" />
								<path className="animated-checkmark__check" fill="none" d="M15 27l7 7 15-15" />
							</svg>
						</div>
						<h4>Purchase complete!</h4>
						{getSuccessMessage()}
						{formData && (
							<VoucherTicket
								isGift={formData.isGift}
								code={voucherCode || '—'}
								amount={formData.amount}
								status="active"
								expiryText={
									formData.expiryDate ? `Expires ${formatStandardDate(formData.expiryDate)}` : ''
								}
								hasMessage={!!formData.message}
								message={formData.message}
								showMessage={!!formData.message}
							/>
						)}
					</div>
				);
			default:
				return null;
		}
	};

	const renderFooter = () => {
		switch (step) {
			case 'login': {
				const showBack = loginMode !== 'initial' && !registrationComplete;
				return (
					<NewModal.Footer>
						{showBack ? (
							<Button
								display={Display.Outline}
								colors={Colors.Grey}
								newStyles
								onClick={() => loginResetRef.current?.()}
								disabled={loginLoading}
							>
								Back
							</Button>
						) : (
							<Button
								display={Display.Outline}
								colors={Colors.Grey}
								newStyles
								onClick={handleClose}
								disabled={loginLoading}
							>
								Cancel
							</Button>
						)}
						{registrationComplete ? (
							<Button display={Display.Solid} colors={Colors.Primary} newStyles onClick={onClose}>
								Done
							</Button>
						) : (
							<Button
								display={Display.Solid}
								colors={Colors.Primary}
								newStyles
								type="submit"
								disabled={loginLoading}
								buttonProps={{
									form: 'purchase-voucher-login',
								}}
							>
								Continue
								{loginLoading && <LoaderInsideButton />}
							</Button>
						)}
					</NewModal.Footer>
				);
			}
			case 'details':
				return (
					<NewModal.Footer>
						<Button display={Display.Outline} colors={Colors.Grey} newStyles onClick={handleClose}>
							Cancel
						</Button>
						<Button
							display={Display.Solid}
							colors={Colors.Primary}
							newStyles
							onClick={() => {
								if (hasTerms && !transactionId) {
									// Save form data and go to terms before creating transaction
									formRef.current?.submit();
								} else {
									formRef.current?.submit();
								}
							}}
							disabled={loading}
						>
							{loading ? 'Processing...' : 'Continue'}
						</Button>
					</NewModal.Footer>
				);
			case 'registerCustomer':
				return (
					<RegisterCustomerStepFooter
						formId={VOUCHER_REGISTER_CUSTOMER_FORM_ID}
						onBack={() => {
							setStep('details');
							scrollModalToTop();
						}}
						loading={registerCustomerLoading}
					/>
				);
			case 'terms':
				return (
					<NewModal.Footer>
						<Button
							display={Display.Outline}
							colors={Colors.Grey}
							newStyles
							onClick={() => {
								setStep('details');
								scrollModalToTop();
							}}
						>
							Back
						</Button>
						<Button
							display={Display.Solid}
							colors={Colors.Primary}
							newStyles
							// Left clickable while unticked so the press can raise the error on the
							// checkbox rather than leaving a dead button.
							disabled={loading}
							onClick={() => {
								if (!termsAccepted) {
									setTermsPromptCount(count => count + 1);
									return;
								}
								if (formData) {
									handleFormSubmit(formData);
								}
							}}
						>
							{loading ? 'Processing...' : 'Continue to payment'}
						</Button>
					</NewModal.Footer>
				);
			case 'payment':
				return (
					<NewModal.Footer>
						<Button
							display={Display.Outline}
							colors={Colors.Grey}
							newStyles
							onClick={() => setStep(hasTerms ? 'terms' : 'details')}
							disabled={paymentProcessing}
						>
							Back
						</Button>
						<Button
							display={Display.Solid}
							colors={Colors.Primary}
							newStyles
							disabled={paymentProcessing}
							onClick={() => {
								scrollModalToTop();
								const form = document.getElementById(PAYMENT_FORM_ID) as HTMLFormElement;
								if (form) {
									form.requestSubmit();
								}
							}}
						>
							{paymentProcessing ? 'Processing...' : 'Pay now'}
						</Button>
					</NewModal.Footer>
				);
			case 'success':
				return (
					<NewModal.Footer>
						<Button display={Display.Solid} colors={Colors.Primary} newStyles onClick={handleClose}>
							Done
						</Button>
					</NewModal.Footer>
				);
			default:
				return null;
		}
	};

	return (
		<NewModal
			isOpen={isOpen}
			label="Purchase Gift Voucher"
			onRequestClose={handleClose}
			modalSize="md"
			className="purchase-gift-voucher-modal"
			shouldCloseOnOverlayClick={false}
			shouldCloseOnEsc={false}
		>
			<NewModal.Header>
				<GiftVoucherStepIndicator steps={steps} currentStepIndex={currentStepIndex} />
			</NewModal.Header>
			{showTimer && (
				<ReservationTimer transactionId={transactionId!} onExpiry={handleTimerExpiry} variant="strip" />
			)}
			<NewModal.Body>{renderStepContent()}</NewModal.Body>
			{renderFooter()}
		</NewModal>
	);
}

export default observer(PurchaseGiftVoucherModal);
